import type { Session } from '../types/auth.types';

/**
 * Where the session lives between page loads.
 *
 * ---------------------------------------------------------------------------
 * THE TRADE-OFF, STATED PLAINLY
 *
 * The preferred design is an HttpOnly, Secure, SameSite cookie that JavaScript
 * cannot read (§16). That is not available: `POST /api/Users/login` returns the
 * JWT in the response body and sets no cookie, so the browser has to hold it
 * somewhere reachable from JS.
 *
 * Given that constraint, this uses `sessionStorage` rather than `localStorage`:
 *
 *   - it is cleared when the tab closes, so a shared or kiosk machine does not
 *     keep an admin session alive indefinitely;
 *   - it is not shared across tabs or windows, narrowing the blast radius;
 *   - it still survives a page refresh, which localStorage's main benefit is.
 *
 * It remains readable by any successful XSS. That is an accepted, documented
 * risk, not an oversight, and it is why the backend must keep enforcing
 * authorization on every endpoint.
 *
 * Only tokens and the user id are stored. Role and permissions are never
 * persisted — they are re-read from `/admin/me` on every page load, so a
 * permission change takes effect without signing in again.
 *
 * MIGRATION PATH: when the backend is changed to set an HttpOnly cookie, this
 * file is the only one that changes — `save`/`load`/`clear` become no-ops and
 * `getAccessToken` returns null so the interceptor stops attaching a header.
 * Nothing else imports `sessionStorage` (§16).
 * ------------------------------------------------------------------------- */

const STORAGE_KEY = 'dc-admin-session';

export function saveSession(session: Session): void {
  try {
    const persisted: Session = {
      userId: session.userId,
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    // Storage blocked or full. The session stays in memory for this page view.
  }
}

export function loadSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as Session).accessToken !== 'string' ||
      typeof (parsed as Session).refreshToken !== 'string' ||
      typeof (parsed as Session).userId !== 'number'
    ) {
      return null;
    }

    const { userId, accessToken, refreshToken } = parsed as Session;
    return { userId, accessToken, refreshToken };
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the in-memory store is cleared by the caller regardless.
  }
}
